import React, { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import Select from "react-select";

import {
  get_rapportagetekst_mappings,
  update_rapportagetekst_mapping,
  get_all_teksten,
} from "../../functions/api";

import { FaTrashAlt } from "react-icons/fa";

import Menu from "../navigation/Menu";
import Nav from "../navigation/Nav";
import Tekst, { LibraryIcon } from "../reports/Tekstenbieb";

function snippet(text, maxLen = 80) {
  if (!text) return "";
  const clean = String(text).replace(/\s+/g, " ").trim();
  return clean.length <= maxLen ? clean : clean.slice(0, maxLen - 1) + "…";
}

function RapportageTekstenComponent() {
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [types, setTypes] = useState([]);
  const [fields, setFields] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [teksten, setTeksten] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  // Openstaande (nog niet opgeslagen) keuzes, gekeyed op `${soortinspectiesid}|${veld_key}`.
  const [pending, setPending] = useState({});

  const tekstById = useMemo(() => {
    const m = {};
    for (const t of teksten) m[t.biebtekstid] = t;
    return m;
  }, [teksten]);

  const loadAll = () => {
    setLoading(true);
    Promise.all([get_rapportagetekst_mappings(), get_all_teksten()])
      .then(async ([mapRes, tekstRes]) => {
        if (!mapRes.ok) {
          const body = await mapRes.json().catch(() => ({}));
          throw new Error(body.error || "Kon mapping niet laden.");
        }
        const mapData = await mapRes.json();

        // get_all_teksten levert { result: [{ biebtekstid, labelnaam, omschrijving,
        // toelichting }, ...] } — exact dezelfde dataset die de Tekstenbieb-popup gebruikt.
        let tekstData = [];
        if (tekstRes.ok) {
          const tekstResponse = await tekstRes.json();
          const items = Array.isArray(tekstResponse?.result)
            ? tekstResponse.result
            : Array.isArray(tekstResponse)
            ? tekstResponse
            : [];
          for (const t of items) {
            const id = parseInt(t.biebtekstid, 10);
            if (!Number.isFinite(id)) continue;
            tekstData.push({
              biebtekstid: id,
              omschrijving: t.omschrijving,
              toelichting: t.toelichting,
              labelnaam: t.labelnaam,
            });
          }
        }

        // CodeIgniter result_array() levert numerieke kolommen als strings.
        // Cast soortinspectiesid + biebtekstids naar number zodat strict-equality
        // matches met `mappings` en `tekstById` consistent werken.
        const normalizedTypes = (mapData.types || []).map((t) => ({
          ...t,
          soortinspectiesid: parseInt(t.soortinspectiesid, 10),
        }));
        const normalizedMappings = (mapData.mappings || []).map((m) => ({
          ...m,
          soortinspectiesid: parseInt(m.soortinspectiesid, 10),
          account_biebtekstid:
            m.account_biebtekstid !== null && m.account_biebtekstid !== undefined
              ? parseInt(m.account_biebtekstid, 10)
              : null,
          factory_biebtekstid:
            m.factory_biebtekstid !== null && m.factory_biebtekstid !== undefined
              ? parseInt(m.factory_biebtekstid, 10)
              : null,
        }));

        setIsOwner(!!mapData.is_owner);
        setTypes(normalizedTypes);
        setFields(mapData.fields || []);
        setMappings(normalizedMappings);
        setTeksten(tekstData);
        if (normalizedTypes.length > 0 && !selectedTypeId) {
          setSelectedTypeId(normalizedTypes[0].soortinspectiesid);
        }
      })
      .catch((error) => {
        console.error("Error loading rapportagetekst mappings:", error);
        Swal.fire({
          title: "Fout!",
          text: error.message || "Kon rapportageteksten niet laden.",
          icon: "error",
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mappingFor = (soortinspectiesid, veld_key) =>
    mappings.find(
      (m) => m.soortinspectiesid === soortinspectiesid && m.veld_key === veld_key
    );

  // Opgeslagen (effectieve) tekst-id voor een veld. Tri-state:
  //   >0   = gekoppelde tekst
  //    0   = expliciet leeggemaakt (account-override die de factory-default overschrijft)
  //   null = niets ingesteld → val terug op factory-default
  // Een account-rij heeft altijd voorrang op de factory-default, óók als die 0 (leeg) is.
  const savedIdFor = (soortinspectiesid, veld_key) => {
    const m = mappingFor(soortinspectiesid, veld_key);
    if (!m) return null;
    return m.account_biebtekstid !== null && m.account_biebtekstid !== undefined
      ? m.account_biebtekstid
      : m.factory_biebtekstid ?? null;
  };

  // Alleen echte wijzigingen tellen: een keuze gelijk aan de opgeslagen waarde is geen wijziging.
  const dirtyEntries = useMemo(() => {
    return Object.entries(pending)
      .map(([key, val]) => {
        const sep = key.indexOf("|");
        const soortinspectiesid = parseInt(key.slice(0, sep), 10);
        const veld_key = key.slice(sep + 1);
        return {
          key,
          soortinspectiesid,
          veld_key,
          biebtekstid: val.biebtekstid,
          tekst: val.tekst,
        };
      })
      .filter((e) => {
        const savedEff = savedIdFor(e.soortinspectiesid, e.veld_key);
        const savedEmpty = !savedEff; // null (niets) of 0 (leeg) → effectief leeg
        const pendingEmpty = !e.biebtekstid; // 0 → leegmaken
        // Leegmaken telt alleen als er nu écht een tekst staat; anders is het een no-op.
        return pendingEmpty ? !savedEmpty : e.biebtekstid !== savedEff;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, mappings]);

  const dirtyKeys = useMemo(
    () => new Set(dirtyEntries.map((e) => e.key)),
    [dirtyEntries]
  );

  const stagePending = (soortinspectiesid, veld_key, tekst) => {
    if (!tekst || tekst.biebtekstid == null) return;
    const key = `${soortinspectiesid}|${veld_key}`;
    setPending((prev) => ({
      ...prev,
      [key]: { biebtekstid: parseInt(tekst.biebtekstid, 10), tekst },
    }));
  };

  // Veld leegmaken: biebtekstid 0 = expliciet leeg (overschrijft factory-default).
  const stageClear = (soortinspectiesid, veld_key) => {
    const key = `${soortinspectiesid}|${veld_key}`;
    setPending((prev) => ({
      ...prev,
      [key]: { biebtekstid: 0, tekst: null },
    }));
  };

  const handleSaveAll = () => {
    if (dirtyEntries.length === 0 || saving) return;
    setSaving(true);
    Promise.all(
      dirtyEntries.map((e) =>
        update_rapportagetekst_mapping(e.soortinspectiesid, e.veld_key, {
          biebtekstid: e.biebtekstid,
        }).then(async (response) => {
          if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.error || "Opslaan mislukt.");
          }
          return true;
        })
      )
    )
      .then(() => {
        Swal.fire({
          title: "Opgeslagen",
          icon: "success",
          timer: 1000,
          showConfirmButton: false,
        });
        setPending({});
        loadAll();
      })
      .catch((error) => {
        console.error("Error saving mappings:", error);
        Swal.fire({ title: "Fout!", text: error.message, icon: "error" });
      })
      .finally(() => setSaving(false));
  };

  const typeOptions = types.map((t) => ({
    value: t.soortinspectiesid,
    label: t.soortnaam || `Type ${t.soortinspectiesid}`,
  }));

  const fieldsByChapter = useMemo(() => {
    const groups = {};
    for (const f of fields) {
      const ch = f.chapter || "Overig";
      if (!groups[ch]) groups[ch] = [];
      groups[ch].push(f);
    }
    return groups;
  }, [fields]);

  const thStyle =
    "px-4 py-3 border-b-2 border-gray-200 bg-gray-50 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider";
  const tdStyle =
    "px-4 py-3 border-b border-gray-200 bg-white text-sm align-middle";

  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, color: "#1f2937" }),
    singleValue: (base) => ({ ...base, color: "#1f2937" }),
    input: (base) => ({ ...base, color: "#1f2937" }),
    option: (base, state) => ({
      ...base,
      color: state.isSelected ? "#ffffff" : "#1f2937",
      backgroundColor: state.isSelected
        ? "#2563eb"
        : state.isFocused
        ? "#e5e7eb"
        : "#ffffff",
      cursor: "pointer",
    }),
  };

  return (
    <div className="select-report-container-main">
      <Nav page="Standaard rapportteksten" />
      <div className="page-wrap-row">
        <Menu className="menu-container" />
        <div className="select-report-container p-6 md:p-10">
          <h1 className="text-2xl font-semibold mb-2 ml-10 mt-10">
            Standaard rapportteksten per inspectietype
          </h1>
          <p className="ml-10 mb-6 text-gray-600 text-sm max-w-3xl">
            Stel per inspectietype in welke tekst uit de Tekstenbieb geladen
            wordt voor elk veld van een nieuw rapport. Wijzigingen gelden
            alleen voor jouw account.
            {!isOwner && (
              <span className="block mt-2 text-amber-700">
                Alleen de bedrijfseigenaar kan deze instellingen wijzigen — je
                ziet de huidige configuratie alleen ter informatie.
              </span>
            )}
          </p>

          <div className="px-10 mb-6 max-w-md">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Inspectietype
            </label>
            <Select
              options={typeOptions}
              value={
                typeOptions.find((o) => o.value === selectedTypeId) || null
              }
              onChange={(opt) => setSelectedTypeId(opt ? opt.value : null)}
              isClearable={false}
              placeholder="Kies een inspectietype..."
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
              styles={selectStyles}
            />
          </div>

          {loading && <p className="ml-10 text-gray-500">Laden...</p>}

          {!loading && selectedTypeId && (
            <div className="select-report-container-1">
              <div className="overflow-x-auto shadow rounded-lg">
                <table className="min-w-full leading-normal">
                  <thead>
                    <tr>
                      <th className={thStyle}>Hoofdstuk</th>
                      <th className={thStyle}>Veld</th>
                      <th className={thStyle}>Gekoppelde tekst</th>
                      <th className={thStyle}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(fieldsByChapter).map((chapter) => (
                      <React.Fragment key={chapter}>
                        {fieldsByChapter[chapter].map((field) => {
                          const key = `${selectedTypeId}|${field.veld_key}`;
                          const pendingEntry = pending[key];
                          const savedId = savedIdFor(
                            selectedTypeId,
                            field.veld_key
                          );
                          const isDirty = dirtyKeys.has(key);

                          // Effectieve keuze: openstaande wijziging heeft voorrang op
                          // de opgeslagen waarde. effId: null=niets, 0=leeg, >0=tekst.
                          let effId;
                          let effTekst = null;
                          if (pendingEntry) {
                            effId = pendingEntry.biebtekstid;
                            effTekst = pendingEntry.tekst; // null bij leegmaken
                          } else {
                            effId = savedId;
                            if (effId && effId > 0)
                              effTekst = tekstById[effId] || null;
                          }
                          const isCleared = effId === 0;
                          const hasText = effId != null && effId > 0;
                          const notInLibrary = hasText && !effTekst;

                          return (
                            <tr key={key} className="hover:bg-gray-50">
                              <td
                                className={`${tdStyle} text-gray-500 whitespace-nowrap`}
                              >
                                {chapter}
                              </td>
                              <td className={`${tdStyle} font-medium`}>
                                {field.label}
                              </td>
                              <td className={tdStyle} style={{ minWidth: 360 }}>
                                <div className="flex items-start gap-3">
                                  <div className="flex-1 min-w-0">
                                    {hasText ? (
                                      <>
                                        <span className="block text-sm font-medium text-blue-700">
                                          {effTekst
                                            ? effTekst.labelnaam ||
                                              "Zonder label"
                                            : `Tekst #${effId}`}
                                        </span>
                                        <span
                                          className={`block text-xs mt-0.5 ${
                                            notInLibrary
                                              ? "text-amber-700 italic"
                                              : "text-gray-500 italic"
                                          }`}
                                        >
                                          {effTekst
                                            ? snippet(effTekst.omschrijving, 140)
                                            : "(niet in jouw bibliotheek)"}
                                        </span>
                                      </>
                                    ) : isCleared ? (
                                      <span className="block text-sm text-gray-500 italic">
                                        Leeg — geen tekst
                                      </span>
                                    ) : (
                                      <span className="block text-sm text-gray-400 italic">
                                        Nog geen tekst gekoppeld
                                      </span>
                                    )}
                                  </div>
                                  {isOwner && (
                                    <div className="shrink-0 flex items-center gap-1">
                                      <Tekst
                                        texts={teksten}
                                        title="Kies een tekst uit de Tekstenbieb"
                                        toParent={(t) =>
                                          stagePending(
                                            selectedTypeId,
                                            field.veld_key,
                                            t
                                          )
                                        }
                                        trigger={
                                          <button
                                            type="button"
                                            title={
                                              hasText
                                                ? "Tekst wijzigen"
                                                : "Tekst kiezen"
                                            }
                                            aria-label={
                                              hasText
                                                ? "Tekst wijzigen"
                                                : "Tekst kiezen"
                                            }
                                            className="group rounded-lg p-1.5 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition"
                                          >
                                            <LibraryIcon />
                                          </button>
                                        }
                                      />
                                      {hasText && (
                                        <button
                                          type="button"
                                          title="Veld leegmaken"
                                          aria-label="Veld leegmaken"
                                          onClick={() =>
                                            stageClear(
                                              selectedTypeId,
                                              field.veld_key
                                            )
                                          }
                                          className="rounded-lg p-1.5 text-gray-500 hover:bg-red-500/20 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-300 transition"
                                        >
                                          <FaTrashAlt size={16} />
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td
                                className={`${tdStyle} whitespace-nowrap`}
                              >
                                {isDirty ? (
                                  <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800">
                                    Niet opgeslagen
                                  </span>
                                ) : isCleared ? (
                                  <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">
                                    Leeg
                                  </span>
                                ) : hasText ? (
                                  <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                                    Ingesteld
                                  </span>
                                ) : (
                                  <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
                                    Niet ingesteld
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {isOwner && (
                <div className="flex items-center justify-end gap-4 mt-4">
                  {dirtyEntries.length > 0 && (
                    <span className="text-sm text-amber-700">
                      {dirtyEntries.length} niet-opgeslagen wijziging
                      {dirtyEntries.length === 1 ? "" : "en"}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={handleSaveAll}
                    disabled={dirtyEntries.length === 0 || saving}
                    className={`inline-flex items-center px-5 py-2 text-sm font-medium rounded-md text-white transition ${
                      dirtyEntries.length === 0 || saving
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {saving
                      ? "Opslaan..."
                      : dirtyEntries.length > 0
                      ? `Opslaan (${dirtyEntries.length})`
                      : "Opslaan"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RapportageTekstenComponent;
